import { useRef, useState } from 'react';
import constate from 'constate';

import { Rotate } from 'services/file.service';
import { AspectRatio } from 'services/clip.service';
import { PAN_ZOOM_FE } from './_pan-zoom.params';
import { getARViewerSize } from './_mediator/pan-zoom.helper';

export interface PanZoomRotate {
  left: number;
  bottom: number;
  rotate: number;
  zoom: number;
  aspectRatio: AspectRatio;
}

export const usePanZoomProvider = () => {
  const rootEl = useRef<HTMLDivElement>(null);
  const viewerEl = useRef<HTMLDivElement>(null);
  const frameEl = useRef<HTMLDivElement>(null);
  const zoomEl = useRef<HTMLDivElement>(null);

  const zoomRectDiff = useRef({ left: 0, bottom: 0 });
  const ARViewerSize = useRef({ width: 0, height: 0 });
  const panZoomRotateRef = useRef(PAN_ZOOM_FE);

  const [panZoomRotate, _setPanZoomRotate] = useState<PanZoomRotate>(PAN_ZOOM_FE);

  const setPanZoomRotate = (payload: Partial<PanZoomRotate>) => {
    panZoomRotateRef.current = { ...panZoomRotate, ...payload };
    _setPanZoomRotate((data) => ({ ...data, ...payload }));
  };

  const setZoomRect = (payload?: { left: number; bottom: number; zoom: Fraction }) => {
    const viewerRect = viewerEl.current!.getBoundingClientRect();
    let left = payload?.left ?? panZoomRotate.left;
    let bottom = payload?.bottom ?? panZoomRotate.bottom;
    const zoom = payload?.zoom ?? panZoomRotate.zoom;

    const frameW = ARViewerSize.current.width * zoom;
    const frameH = ARViewerSize.current.height * zoom;

    const width = frameW / zoom;
    const height = frameH / zoom;

    left = left - (width - frameW) / 2;
    bottom = bottom - (height - frameH) / 2;
    const top = viewerRect.height - (bottom + height);
    const right = viewerRect.width - (left + width);

    if (left < 0) left = 0;
    else if (right < 0) left = viewerRect.width - width;
    if (top < 0) bottom = viewerRect.height - height;
    else if (bottom < 0) bottom = 0;

    zoomEl.current!.style.width = width + 'px';
    zoomEl.current!.style.height = height + 'px';
    zoomEl.current!.style.left = left + 'px';
    zoomEl.current!.style.bottom = bottom + 'px';

    zoomRectDiff.current = { left, bottom };
  };

  const setARViewerSize = (val: AspectRatio, rotate: Rotate | null) => {
    ARViewerSize.current = getARViewerSize(viewerEl.current!, val, rotate);
  };

  return {
    rootEl,
    viewerEl,
    frameEl,
    zoomEl,
    zoomRectDiff,
    panZoomRotate,
    setARViewerSize,
    setPanZoomRotate,
    panZoomRotateRef,
    ARViewerSize,
    setZoomRect,
  };
};

export const [PanZoomProvider, usePanZoomContext, usePanZoomSetter] = constate(
  usePanZoomProvider,
  (state) => state,
  (state) => state.setPanZoomRotate
);
